import { useQueryClient } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { useForm, Controller } from "react-hook-form";

import Input from "../../ChildComponents/Input";
import BrandButton from "../../ChildComponents/BrandButton";
import { Rolechema } from "../validation/roleValidation";

import { useUploadImage, useUpdateImages } from "../../store/api/WebAdsApi";
import SmartImageUpload from "../../ChildComponents/SmartImageUploader";

const IMAGE_URL = process.env.REACT_APP_IMAGE_URL;

function WebAdsFrom({ data, editId, onSuccessCreated }) {
  const queryClient = useQueryClient();
  const updateImages = useUpdateImages(editId?._id);
  const [loading, setLoading] = useState(false);
  const [edited, setEdited] = useState(editId ? true : false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    control,
  } = useForm({
    resolver: yupResolver(Rolechema),
  });

  // ------------ RESET DATA (Create & Edit Mode) ----------------
  useEffect(() => {
    if (editId) {
      reset({
        sectionID: editId?.sectionID || "",
        image: editId?.image ? `${editId.image}` : null,
        fontStatus: editId?.fontStatus || false,
        url: editId?.url || "",
      });
      setEdited(true);
    } else {
      reset({
        sectionID: "",
        image: null,
        fontStatus: false,
        url: "",
      });
      setEdited(false);
    }
  }, [editId, reset]);

  const uploadImage = useUploadImage();

  // ---------------- CREATE / UPDATE MUTATION ----------------
  const mutation = useMutation({
    mutationFn: (data) => {
      return editId ? updateImages(data) : uploadImage(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["images"]);
      setLoading(false);
      toast.success(editId ? "Updated Successfully" : "Created Successfully");
      reset();
      onSuccessCreated(1);
    },
    onError: (error) => {
      toast.error(error.message);
      setLoading(false);
    },
  });

  // --------------------- SUBMIT FORM -------------------------
  const onSubmit = (formData) => {
    setLoading(true);

    try {
      const data = new FormData();

      if (!formData.image) {
        toast.warning("Image is required!");
        setLoading(false);
        return;
      }

      data.append("sectionID", formData.sectionID);
      data.append("fontStatus", formData.fontStatus);
      data.append("url", formData.url);
      data.append("image", formData.image);

      data.forEach((value, key) => {
        console.log(key, value);
      });

      mutation.mutate(data);
    } catch (error) {
      setLoading(false);
      toast.error(error.message);
    }
  };

  // ------------------------ UI START -------------------------
  return (
    <div className="container-fluid p-0">
      {/* Header Section */}
      <div
        className="mb-4 p-4 rounded-4 position-relative overflow-hidden"
        style={{
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          boxShadow: "0 10px 30px rgba(102, 126, 234, 0.3)",
        }}
      >
        <div className="d-flex align-items-center">
          <div
            className="d-flex align-items-center justify-content-center rounded-3 me-3"
            style={{
              width: "48px",
              height: "48px",
              background: "rgba(255, 255, 255, 0.2)",
              backdropFilter: "blur(10px)",
            }}
          >
            <i
              className="material-icons"
              style={{ color: "white", fontSize: "28px" }}
            >
              {editId ? "edit" : "video_library"}
            </i>
          </div>
          <div>
            <h4 className="text-white mb-1 fw-bold">
              {editId ? "Edit Ads" : "Add New Ads"}
            </h4>
            <p
              className="text-white mb-0"
              style={{ opacity: 0.9, fontSize: "0.9rem" }}
            >
              {editId ? "Update Ads" : "Upload Ads"}
            </p>
          </div>
        </div>
      </div>

      {/* Form Section */}
      <form onSubmit={handleSubmit(onSubmit)}>
        <div
          className="p-4 rounded-4 shadow-sm"
          style={{
            background: "white",
            border: "1px solid #e2e8f0",
          }}
        >
          {/* Basic Information Section */}
          <div className="mb-4">
            <div className="d-flex align-items-center mb-3">
              <div
                className="d-flex align-items-center justify-content-center rounded-2 me-2"
                style={{
                  width: "32px",
                  height: "32px",
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                }}
              >
                <i
                  className="material-icons"
                  style={{ color: "white", fontSize: "18px" }}
                >
                  info
                </i>
              </div>
              <h5 className="mb-0 fw-semibold" style={{ color: "#2d3748" }}>
                Basic Information
              </h5>
            </div>

            <div className="row">
              <div className="col-lg-4 col-md-6">
                <Input
                  icon="layers"
                  {...register("sectionID")}
                  error={errors.sectionID?.message}
                  type="text"
                  placeholder="Enter Section ID"
                  label="Section ID"
                  disabled
                />
              </div>

              <div className="col-lg-4 col-md-6">
                <Input
                  icon="link"
                  {...register("url")}
                  error={errors.url?.message}
                  type="text"
                  placeholder="Enter URL"
                  label="URL"
                />
              </div>

              <div className="col-lg-4 col-md-6 mt-4">
                <div className="form-check form-switch">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    {...register("fontStatus")}
                    defaultChecked={editId?.fontStatus}
                  />
                  <label className="form-check-label">Font Status</label>
                </div>
              </div>
            </div>
          </div>

          {/* Image Section */}
          <div className="mb-4">
            <div className="d-flex align-items-center mb-3">
              <div
                className="d-flex align-items-center justify-content-center rounded-2 me-2"
                style={{
                  width: "32px",
                  height: "32px",
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                }}
              >
                <i
                  className="material-icons"
                  style={{ color: "white", fontSize: "18px" }}
                >
                  description
                </i>
              </div>
              <h5 className="mb-0 fw-semibold" style={{ color: "#2d3748" }}>
                Content & Media
              </h5>
            </div>

            <div className="row">
              <div className="col-lg-4">
                <div
                  className="card shadow-lg border-0 mb-4"
                  style={{ borderRadius: "20px", overflow: "hidden" }}
                >
                  <div
                    className="card-header text-white py-3"
                    style={{
                      background:
                        "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    }}
                  >
                    <h5 className="mb-0 text-white d-flex align-items-center">
                      <i className="material-icons me-2">image</i>
                      Featured Image
                    </h5>
                  </div>
                  <div className="card-body p-4">
                    <Controller
                      name="image"
                      control={control}
                      defaultValue={null}
                      render={({ field }) => (
                        <SmartImageUpload
                          multiple={false}
                          limit={1}
                          files={field.value ? [field.value] : []}
                          onSelectFiles={(url) => {
                            field.onChange(url.data[0].image || "");
                          }}
                        />
                      )}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="d-flex align-items-center justify-content-between pt-3 border-top">
            <div
              className="d-flex align-items-center text-muted"
              style={{ fontSize: "0.85rem" }}
            >
              <i className="material-icons me-1" style={{ fontSize: "18px" }}>
                schedule
              </i>
              {editId
                ? "Last updated: " + new Date().toLocaleDateString()
                : "Fill all required fields"}
            </div>

            <div className="d-flex gap-2">
              {editId && (
                <button
                  type="button"
                  className="btn btn-light rounded-3 px-4"
                  onClick={() => reset()}
                  style={{
                    border: "1px solid #e2e8f0",
                    transition: "all 0.3s ease",
                  }}
                >
                  <i
                    className="material-icons me-2"
                    style={{ fontSize: "18px", verticalAlign: "middle" }}
                  >
                    refresh
                  </i>
                  Reset
                </button>
              )}

              <BrandButton
                type="submit"
                title={
                  loading
                    ? "Processing..."
                    : editId
                    ? "Update Ads"
                    : "Upload Ads"
                }
                disabled={loading}
                className="rounded-3 px-4 shadow"
                style={{
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  border: "none",
                  minWidth: "160px",
                }}
              />
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}

export default WebAdsFrom;
